/**
 * Recharts Grouped Bar — multi-series bars rendered side-by-side within each
 * category (no stacking). Matches GoodData's "dual-metric per row" pattern
 * (e.g., Active Users 2.11 Most Active Buildings — Sessions + Active Users
 * shown together per node).
 */

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { GroupedBarChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = GroupedBarChartProps & CommonChartProps;

export function RechartsGroupedBar({
  data,
  xKey,
  seriesKeys,
  orientation = 'vertical',
  xLabel,
  yLabel,
}: Props): JSX.Element {
  const theme = chartTheme('recharts');
  const palette = theme.theme.categoricalPalette;
  const isHorizontal = orientation === 'horizontal';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={theme.axis.stroke} />
        {isHorizontal ? (
          <>
            <XAxis type="number" tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
            <YAxis type="category" dataKey={xKey} tick={theme.axis.tick} stroke={theme.axis.stroke} width={160} />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} tick={theme.axis.tick} stroke={theme.axis.stroke} label={xLabel} />
            <YAxis tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
          </>
        )}
        <Tooltip
          content={({ active, payload, label }) =>
            active && payload && payload.length > 0 ? (
              <DrillAwareTooltip
                header={String(label ?? '')}
                rows={payload.map((p, i) => ({
                  color: (p.color as string) ?? palette[i % palette.length],
                  label: String(p.name ?? p.dataKey ?? ''),
                  value: (p.value as number) ?? 0,
                }))}
              />
            ) : null
          }
        />
        {seriesKeys.length > 1 && (
          <Legend wrapperStyle={{ fontFamily: theme.legend.fontFamily, fontSize: theme.legend.fontSize }} />
        )}
        {seriesKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={palette[i % palette.length]} radius={[2, 2, 2, 2]} />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
